import React, { useEffect, useRef } from 'react';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { Transition } from '@headlessui/react';

interface Props {
  title?: string;
  header?: JSX.Element;
  show: boolean;
  onClose: () => void;
  children: JSX.Element;
  widthClassName?: string;
  heightClassName?: string;
  closeButtonColor?: string;
}

const SideSliderV2 = ({
  show,
  onClose,
  children,
  title,
  header,
  closeButtonColor,
  widthClassName = 'w-[500px]',
  heightClassName = 'h-[600px]',
}: Props) => {
  const sliderContainerRef = useRef<HTMLDivElement>(null);
  const sliderChildrenRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const listener = (event: MouseEvent) => {
      const targetElement = event.target as HTMLElement;

      if (
        sliderChildrenRef.current &&
        !sliderChildrenRef.current.contains(targetElement) &&
        sliderContainerRef.current &&
        sliderContainerRef.current.contains(targetElement)
      ) {
        onClose();
      }
    };

    document.body.addEventListener('click', listener);

    return () => {
      document.body.removeEventListener('click', listener);
    };
  }, [sliderContainerRef.current, sliderChildrenRef.current]);

  return (
    <div ref={sliderContainerRef} className="fixed bottom-0 left-0 z-50">
      <Transition show={show}>
        <div className="w-screen h-screen" style={{ perspective: '1500px' }}>
          <Transition.Child
            className={`${widthClassName} ${heightClassName} fixed bottom-0 rtl:left-0 ltr:right-0 shadow`}
            enter="transition-all ease-in-out duration-300 transform"
            enterFrom="rtl:-translate-x-full ltr:translate-x-full"
            enterTo="translate-x-0"
            leave="transition-all ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="rtl:-translate-x-full ltr:translate-x-full"
          >
            <div
              ref={sliderChildrenRef}
              className={`w-full h-full border-gray-50 border-s bg-white`}
            >
              <div className="flex-grow text-xl font-semibold">
                <div className="flex w-full justify-between items-center relative ">
                  <div className="flex items-center first:ms-0">
                    {title ? (
                      <div className=" p-4  flex w-full items-center">
                        <div className="ms-2 flex items-center first:ms-0">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                            name="filter"
                          >
                            <path
                              fill=""
                              fillRule="evenodd"
                              d="M5 7a1 1 0 000 2h14a1 1 0 100-2H5zm2 5a1 1 0 011-1h8a1 1 0 110 2H8a1 1 0 01-1-1zm3 4a1 1 0 011-1h2a1 1 0 110 2h-2a1 1 0 01-1-1z"
                              clipRule="evenodd"
                            ></path>
                          </svg>
                        </div>
                        <div className="ms-1 flex items-center first:ms-0">
                          <ReturnTranslatedText
                            text={`Slider.titles.${title}`}
                          />
                        </div>
                      </div>
                    ) : (
                      header
                    )}
                  </div>
                  <div
                    className={`flex items-center first:ms-0 absolute top-4  ltr:right-0 rtl:left-0`}
                  >
                    <button
                      onClick={onClose}
                      type="button"
                      className={`leading-normal inline-flex items-center font-normal spark-button-focus  h-8 text-md px-4 bg-transparent border-0 text-${
                        closeButtonColor || 'gray-800'
                      } hover:text-red-500 hover:bg-gray-25 active:bg-gray-25 rounded-md"`}
                      aria-disabled="false"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                        name="close"
                        className=""
                      >
                        <path
                          fill=""
                          fillRule="evenodd"
                          d="M5.293 5.293a1 1 0 011.414 0l12 12a1 1 0 01-1.414 1.414l-12-12a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        ></path>
                        <path
                          fill=""
                          fillRule="evenodd"
                          d="M18.707 5.293a1 1 0 010 1.414l-12 12a1 1 0 01-1.414-1.414l12-12a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        ></path>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              {children}
            </div>
          </Transition.Child>
        </div>
      </Transition>
    </div>
  );
};

export default SideSliderV2;
